/**
 * DNA Inheritance Service
 * MVP: Implements 2-tier DNA fallback: Organization → Generic Defaults
 * (Single-org-single-brand model - DNA stored at org level during onboarding)
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/structured-logger';

// Use looser types for client-side to handle nulls from database
export interface EffectiveDNA {
  brand_voice: string;
  target_audience?: string;
  lexicon?: string[];
  jargon_blacklist?: string[];
  content_pillars?: string[];
  tone_analysis?: Record<string, number>;
  rhetorical_devices?: string[];
  exemplar_1?: string;
  exemplar_2?: string;
  exemplar_3?: string;
}

export type DNASource = 'organization' | 'generic'; // MVP: Using org level, not brand

export interface DNAResult {
  dna: EffectiveDNA;
  source: DNASource;
  warnings: string[];
  quality: 'high' | 'medium' | 'low';
}

export class DNAInheritanceService {
  private static logger = logger.child({ component: 'DNAInheritanceService' });

  /**
   * Get effective DNA with 2-tier fallback chain (MVP simplified)
   * Priority: Organization DNA → Generic defaults
   */
  static async getEffectiveDNA(
    organizationId: string
  ): Promise<DNAResult> {
    const warnings: string[] = [];
    
    this.logger.info('Resolving DNA', { organizationId });

    // Tier 1: Organization DNA (MVP: DNA stored at org level during onboarding)
    const orgDNA = await this.getOrganizationDNA(organizationId);
    if (orgDNA?.brand_voice) {
      this.logger.info('Using organization DNA', { organizationId, hasBrandVoice: true });
      return { 
        dna: orgDNA, 
        source: 'organization', 
        warnings: [],
        quality: 'high'
      };
    }
    
    warnings.push('No organization DNA found, using generic defaults');
    this.logger.warn('No DNA found, using generic defaults', { organizationId });
    
    // Tier 2: Generic defaults
    return {
      dna: this.getGenericDefaults(),
      source: 'generic',
      warnings,
      quality: 'low'
    };
  }
  
  /**
   * Get organization-level DNA (MVP: single-org-single-brand model)
   */
  private static async getOrganizationDNA(organizationId: string): Promise<EffectiveDNA | null> {
    const { data, error } = await supabase
      .from('organization_content_dna')
      .select(`
        brand_voice, target_audience,
        lexicon, jargon_blacklist, content_pillars,
        tone_analysis, rhetorical_devices,
        exemplar_1, exemplar_2, exemplar_3
      `)
      .eq('organization_id', organizationId)
      .maybeSingle();
    
    if (error) {
      this.logger.error('Error fetching organization DNA', error, { organizationId });
      return null;
    }
    
    if (!data?.brand_voice) return null;
    
    return data as EffectiveDNA;
  }
  
  /**
   * Generic fallback DNA for when no custom DNA exists
   */
  private static getGenericDefaults(): EffectiveDNA {
    return {
      brand_voice: "Professional, clear, and value-focused B2B tone",
      target_audience: "B2B decision-makers and product teams",
      lexicon: ["solution", "platform", "integration", "workflow", "productivity"],
      jargon_blacklist: ["synergy", "paradigm shift", "game-changer", "disruptive", "leverage"],
      content_pillars: ["product value", "customer success", "industry insights"],
      tone_analysis: {
        professional: 80,
        conversational: 60,
        technical: 50,
        authoritative: 70,
        playful: 20,
        empathetic: 50
      },
      rhetorical_devices: ["problem-solution", "social proof", "data-driven"]
    };
  }

  /**
   * Check DNA status for UI indicators (MVP: simplified to org-level)
   */
  static async checkDNAStatus(
    organizationId: string
  ): Promise<{
    hasBrand: boolean;
    hasOrg: boolean;
    source: DNASource;
  }> {
    const orgDNA = await this.getOrganizationDNA(organizationId);
    
    const hasOrg = !!orgDNA?.brand_voice;
    const hasBrand = false; // MVP: Brand-level DNA not used
    
    const source: DNASource = hasOrg ? 'organization' : 'generic';
    
    return { hasBrand, hasOrg, source };
  }
}
